import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { IonRouterOutlet } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";
import { IosGestureService } from "src/app/_services/ios-gesture.service";
import { GoogleMapsService } from "src/app/_services/maps";
import { osService } from "src/app/_services/osService.service";
import { Position, SearchPosition } from "src/app/helpers/types";

declare var google: any;

interface MapOptions {
  zoom: number;
  center: google.maps.LatLng;
  disableDefaultUI: boolean;
  maxZoom?: number;
  minZoom?: number;
  zoomControl: boolean;
  zoomControlOptions?: {
    position: google.maps.ControlPosition;
  };
  clickableIcons: boolean;
  gestureHandling?: string;
  rotateControl?: boolean;
  headingInteractionEnabled?: boolean;
  tiltInteractionEnabled?: boolean;
}

interface MarkerOptions {
  position: google.maps.LatLng;
  map: google.maps.Map;
  animation?: google.maps.Animation;
  draggable: boolean;
  title?: string;
  visible?: boolean;
  icon?: {
    url: string;
    scaledSize: google.maps.Size;
    anchor?: google.maps.Point;
  };
}

@Component({
  selector: "app-map",
  templateUrl: "./map.component.html",
  styleUrls: ["./map.component.scss"],
  standalone: false,
})
export class MapComponent implements OnInit, OnChanges, OnDestroy {
  @Input() currentLocation?: { latitude: number; longitude: number };
  @Input() markersList: Position[] = [];
  @Input() mapOptions: Partial<MapOptions> = {};
  @Input() markerOptions: Partial<MarkerOptions> = {};
  @Input() enableDrag: boolean = true;
  @Input() enableZoom: boolean = true;
  @Input() defaultZoom: number = 15;
  @Input() defaultCenter: { lat: number; lng: number } = {
    lat: 35.83766268383463,
    lng: 10.622713348112542,
  };

  @Output() dragStart: EventEmitter<void> = new EventEmitter<void>();
  @Output() dragEnd: EventEmitter<void> = new EventEmitter<void>();
  @Output() draging: EventEmitter<void> = new EventEmitter<void>();
  @Output() openSaveModal: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() mapReady: EventEmitter<google.maps.Map | null> = new EventEmitter<google.maps.Map | null>();
  @Output() markerClick: EventEmitter<{ marker: any; position: any }> = new EventEmitter<{ marker: any; position: any }>();

  @ViewChild('mapElement', { static: true }) mapElement!: ElementRef;

  // Services
  directionsService: google.maps.DirectionsService | null = null;
  directionsDisplay: google.maps.DirectionsRenderer | null = null;

  // Map instances
  map: google.maps.Map | null = null;
  marker: google.maps.Marker | null = null;
  userInfoWindow: google.maps.InfoWindow | null = null;
  currentInfoWindow: google.maps.InfoWindow | null = null;

  // State
  mapTouched: boolean = false;
  isMapInitialized: boolean = false;
  searchPosition?: SearchPosition | null;
  startPosition: google.maps.LatLng | null = null;
  headerText: string = this.translate.instant("PROVIDER.MAP.DEPLACER_ICON");

  // Observables
  private destroy$ = new Subject<void>();
  private mapResizeSubject = new Subject<void>();

  // Constants
  private getDefaultMapOptions(): MapOptions {
    return {
      zoom: 15,
      center: new google.maps.LatLng(35.83766268383463, 10.622713348112542),
      disableDefaultUI: true,
      zoomControl: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
      clickableIcons: false,
      minZoom: 5,
      // La sélection de position se fait en centrant la carte : elle doit rester
      // orientée au nord. On verrouille la rotation/inclinaison (utile si une
      // vector map "mapId" est configurée) pour éviter les rotations involontaires.
      rotateControl: false,
      headingInteractionEnabled: false,
      tiltInteractionEnabled: false,
      // gestureHandling: 'cooperative',
    };
  }

  private getDefaultMarkerOptions(): MarkerOptions {
    return {
      position: new google.maps.LatLng(35.83766268383463, 10.622713348112542),
      map: null as any,
      animation: google.maps.Animation.DROP,
      draggable: false,
      title: "Votre position",
      visible: true,
      icon: {
        url: "/assets/icon/blue_location_icon.png",
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 20),
      },
    };
  }

  constructor(
    private googleMapsService: GoogleMapsService,
    private osService: osService,
    private translate: TranslateService,
    private iosGestureService: IosGestureService,
    @Optional() private ionRouterOutlet?: IonRouterOutlet
  ) {
    this.setupMapResizeHandler();
  }

  ngOnInit(): void {
    // Désactiver le swipe-back iOS pour cette page
    this.iosGestureService.registerOutlet(this.ionRouterOutlet);
    this.iosGestureService.disableSwipeBack();
    this.initializeMap();
    this.subscribeToSearchLocation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentLocation'] && this.isMapInitialized) {
      this.updateMarkerPosition();
    }

    if (changes['markersList'] && this.isMapInitialized) {
      this.updateMarkers();
    }

    if (changes['mapOptions'] && this.isMapInitialized) {
      this.updateMapOptions();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupMap();
    // Réactiver le swipe-back iOS quand on quitte la page
    this.iosGestureService.enableSwipeBack();
    this.iosGestureService.unregisterOutlet();
  }

  // Public API Methods
  public getMap(): google.maps.Map | null {
    return this.map;
  }

  public getMarker(): google.maps.Marker | null {
    return this.marker;
  }

  public setCenter(lat: number, lng: number): void {
    if (this.map) {
      const position = new google.maps.LatLng(lat, lng);
      this.map.setCenter(position);
      this.startPosition = position;
    }
  }

  public setZoom(zoom: number): void {
    if (this.map) {
      this.map.setZoom(zoom);
    }
  }

  public panTo(lat: number, lng: number): void {
    if (this.map) {
      this.map.panTo({ lat, lng });
    }
  }

  public centerOnCurrentLocation(): void {
    if (this.currentLocation && this.map) {
      const position = new google.maps.LatLng(
        this.currentLocation.latitude,
        this.currentLocation.longitude
      );
      this.map.setCenter(position);
    }
  }

  public addMarker(position: Position, options?: Partial<MarkerOptions>): google.maps.Marker | null {
    if (!this.map) return null;

    const markerOptions: MarkerOptions = {
      ...this.getDefaultMarkerOptions(),
      ...options,
      position: new google.maps.LatLng(position.coords.latitude, position.coords.longitude),
      map: this.map,
    };

    const marker = new google.maps.Marker(markerOptions);

    if (position.label) {
      marker.addListener('click', () => {
        this.handleMarkerClick(marker, position);
      });
    }

    return marker;
  }

  public removeMarker(marker: google.maps.Marker): void {
    if (marker) {
      google.maps.event.clearInstanceListeners(marker);
      marker.setMap(null);
    }
  }

  public clearMarkers(): void {
    if (this.marker) {
      this.removeMarker(this.marker);
      this.marker = null;
    }
  }

  public refreshMap(): void {
    if (this.map) {
      google.maps.event.trigger(this.map, 'resize');
    }
  }

  // Private Methods
  private initializeMap(): void {
    if (typeof google !== "undefined") {
      this.loadGoogleMaps();
    } else {
      this.googleMapsService.initMapUrl();
      setTimeout(() => this.initializeMap(), 1000);
    }
  }

  private loadGoogleMaps(): void {
    this.googleMapsService
      .getGoogleMapsLoaded()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (isLoaded: boolean) => {
          if (isLoaded) {
            this.setupMapServices();
            this.waitForMapElement();
          } else {
            console.error("Google Maps API failed to load");
          }
        },
        error: (error) => console.error("Error loading Google Maps:", error)
      });
  }

  private setupMapServices(): void {
    this.userInfoWindow = new google.maps.InfoWindow();
    this.currentInfoWindow = new google.maps.InfoWindow();
    this.directionsService = new google.maps.DirectionsService();
    this.directionsDisplay = new google.maps.DirectionsRenderer();
  }

  private waitForMapElement(): void {
    const maxAttempts = 15;
    let attempts = 0;

    const checkElement = () => {
      attempts++;
      const mapElement = this.mapElement?.nativeElement || document.getElementById("map");

      if (mapElement) {
        this.ensureMapElementVisible(mapElement);
        this.createMapInstance(mapElement);
      } else if (attempts < maxAttempts) {
        setTimeout(checkElement, 200);
      } else {
        console.error("Map element not found after", maxAttempts, "attempts");
      }
    };

    checkElement();
  }

  private ensureMapElementVisible(mapElement: HTMLElement): void {
    const styles = {
      display: 'block',
      visibility: 'visible',
      opacity: '1',
      width: '100vw',
      height: '100vh',
      position: 'absolute',
      top: '0',
      left: '0',
      zIndex: '1',
    };

    Object.assign(mapElement.style, styles);
  }

  private createMapInstance(mapElement: HTMLElement): void {
    this.startPosition = this.getStartPosition();

    const mapOptions: MapOptions = {
      ...this.getDefaultMapOptions(),
      ...this.mapOptions,
      zoom: this.defaultZoom,
      center: this.startPosition,
      minZoom: 5
    };

    try {
      this.map = new google.maps.Map(mapElement, mapOptions);
      this.isMapInitialized = true;

      this.setupMapEventListeners();
      this.createUserMarker();
      this.createMarkers();

      if (this.directionsDisplay) {
        this.directionsDisplay.setMap(this.map);
      }

      this.mapReady.emit(this.map);
      this.refreshMap();

    } catch (error) {
      console.error("Error creating map:", error);
    }
  }

  private getStartPosition(): google.maps.LatLng {
    if (this.currentLocation) {
      return new google.maps.LatLng(
        this.currentLocation.latitude,
        this.currentLocation.longitude
      );
    }
    return new google.maps.LatLng(this.defaultCenter.lat, this.defaultCenter.lng);
  }

  private setupMapEventListeners(): void {
    if (!this.map) return;

    if (this.enableDrag) {
      this.map.addListener("drag", () => this.handleMapDrag());
      this.map.addListener("dragend", () => this.handleMapDragEnd());
      this.map.addListener("dragstart", () => this.handleMapDragStart());
    }

    // Synchroniser le marqueur avec le centre de la carte en temps réel
    this.map.addListener("center_changed", () => this.handleMapCenterChanged());

    this.map.addListener("idle", () => this.handleMapIdle());
  }

  private createUserMarker(): void {
    if (!this.map || !this.startPosition) return;

    const markerOptions: MarkerOptions = {
      ...this.getDefaultMarkerOptions(),
      ...this.markerOptions,
      position: this.startPosition,
      map: this.map,
    };

    try {
      this.marker = new google.maps.Marker(markerOptions);

      // Ajouter un événement de clic au marqueur principal
      this.marker?.addListener('click', () => {
        this.handleUserMarkerClick();
      });

      this.updateMarkerLocation();
    } catch (error) {
      console.error("Error creating user marker:", error);
    }
  }

  private createMarkers(): void {
    if (!this.map) return;

    this.markersList.forEach((position) => {
      this.addMarker(position);
    });
  }

  private updateMarkerPosition(): void {
    if (!this.marker || !this.currentLocation) return;

    const newPosition = new google.maps.LatLng(
      this.currentLocation.latitude,
      this.currentLocation.longitude
    );

    // Sauvegarder l'animation actuelle
    const currentAnimation = this.marker?.getAnimation();

    this.marker.setPosition(newPosition);
    this.startPosition = newPosition;

    // Restaurer l'animation si elle était active
    if (currentAnimation) {
      this.marker.setAnimation(currentAnimation);
    }

    // Centrer la carte sur la nouvelle position du marqueur
    if (this.map) {
      this.map.setCenter(newPosition);
    }

    this.updateMarkerLocation();
  }

  private updateMarkers(): void {
    // Clear existing markers and recreate them
    this.createMarkers();
  }

  private updateMapOptions(): void {
    if (!this.map) return;

    const newOptions = { ...this.getDefaultMapOptions(), ...this.mapOptions };
    // Note: Some options cannot be updated after map creation
    // This is a simplified implementation
  }

  private updateMarkerLocation(): void {
    if (!this.startPosition) return;

    const markerPosition: Position = {
      label: "",
      coords: {
        latitude: this.startPosition.lat(),
        longitude: this.startPosition.lng(),
      },
    };

    this.osService.setMarkerLocation(markerPosition);
  }

  private handleMapDrag(): void {
    this.draging.emit();
    // Le marqueur est maintenant synchronisé par center_changed
  }

  private handleMapCenterChanged(): void {
    // Synchroniser le marqueur avec le centre de la carte en temps réel
    this.updateMarkerPositionFromMap();
  }

  private handleMapDragEnd(): void {
    this.updateMarkerPositionFromMap();
    this.handleDragStart();
    this.dragEnd.emit();

    // Arrêter l'animation de rebond quand le drag se termine
    if (this.marker) {
      this.stopBounce(this.marker);
    }
  }

  private handleMapDragStart(): void {
    this.dragStart.emit();

    // Démarrer l'animation de rebond quand le drag commence
    if (this.marker) {
      this.setBounce(this.marker);
    }
  }

  private handleMapIdle(): void {
    // Map has finished loading and is ready for interaction
  }

  private handleMarkerClick(marker: google.maps.Marker, position: Position): void {
    this.markerClick.emit({ marker, position });

    if (this.currentInfoWindow) {
      this.currentInfoWindow.close();
    }

    const infowindow = new google.maps.InfoWindow({
      content: position.label,
    });

    infowindow.open(this.map, marker);
    this.currentInfoWindow = infowindow;
    this.setBounce(marker);

    // Arrêter l'animation après 1.4 secondes pour les clics sur les marqueurs
    setTimeout(() => {
      if (marker) {
        this.stopBounce(marker);
      }
    }, 1400);
  }

  private updateMarkerPositionFromMap(): void {
    if (!this.map || !this.marker) return;

    const newCenter = this.map.getCenter();
    if (!newCenter) return;

    // Sauvegarder l'animation actuelle
    const currentAnimation = this.marker?.getAnimation();

    this.marker.setPosition(newCenter);

    // Restaurer l'animation si elle était active
    if (currentAnimation) {
      this.marker.setAnimation(currentAnimation);
    }

    const newPosition = {
      lat: newCenter.lat(),
      lng: newCenter.lng(),
    };

    localStorage.setItem("markerPosition", JSON.stringify(newPosition));

    const markerPosition: Position = {
      label: "",
      coords: {
        latitude: newPosition.lat,
        longitude: newPosition.lng,
      },
    };

    this.osService.setMarkerLocation(markerPosition);
  }

  private handleUserMarkerClick(): void {
    if (!this.marker) return;

    // Déclencher l'animation de rebond pour le marqueur principal
    this.setBounce(this.marker);

    // Arrêter l'animation après 1.4 secondes pour les clics
    setTimeout(() => {
      if (this.marker) {
        this.stopBounce(this.marker);
      }
    }, 1400);

    // Émettre l'événement de clic
    const markerPosition = this.marker.getPosition();
    if (markerPosition) {
      const position: Position = {
        label: "Votre position",
        coords: {
          latitude: markerPosition.lat(),
          longitude: markerPosition.lng(),
        },
      };

      this.markerClick.emit({ marker: this.marker, position });
    }
  }

  private setBounce(marker: google.maps.Marker): void {
    // Arrêter toute animation en cours
    marker.setAnimation(null);

    // Démarrer l'animation de rebond
    marker.setAnimation(google.maps.Animation.BOUNCE);
  }

  private stopBounce(marker: google.maps.Marker): void {
    // Arrêter l'animation de rebond
    if (marker) {
      marker.setAnimation(null);
    }
  }

  private handleDragStart(): void {
    this.mapTouched = true;
    this.headerText = this.translate.instant("PROVIDER.MAP.PLACEMENT_ERROR");
  }

  private setupMapResizeHandler(): void {
    this.mapResizeSubject
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(100),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.refreshMap();
      });
  }

  private subscribeToSearchLocation(): void {
    this.osService.SearchLocationSubject$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (searchPosition) => {
          this.searchPosition = searchPosition;
          if (searchPosition?.title && searchPosition?.position?.[0]) {
            const position = searchPosition.position[0];
            this.currentLocation = {
              latitude: position.lat,
              longitude: position.long,
            };
            this.updateMarkerPosition();
            // La carte est déjà centrée par updateMarkerPosition()
          }
        },
        error: (error) => console.error("Error in search location subscription:", error)
      });
  }

  private cleanupMap(): void {
    if (this.marker) {
      this.removeMarker(this.marker);
    }

    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map);
    }

    if (this.directionsDisplay) {
      this.directionsDisplay.setMap(null);
    }
  }

  // Legacy methods for backward compatibility
  moveMapToLocation(lat: number, lng: number, zoom: number): void {
    this.panTo(lat, lng);
    if (zoom) this.setZoom(zoom);
  }

  calculateRoute(): void {
    // Implementation for route calculation
  }

  traceRoute(service: any, display: any, request: any): void {
    // Implementation for route tracing
  }
}
